import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Layout, Grid } from 'antd';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import useMessageFetching from '../hooks/useMessageFetching';
import useContactStatus from '../hooks/useContactStatus';
import MessageHeader from './MessageHeader';
import MessageContent from './MessageContent';
import MessageDetailsDrawer from './MessageDetailsDrawer';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import MessageFooter from './MessageFooter/MessageFooter';
import useSocket from '../../../Hooks/useSocket';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import useSocketEvent from '../../../Hooks/useSocketEvent';
import { Resource, Action } from '../../../Constants/socket';
import { sendMessage } from '../MessagesServices';

const { useBreakpoint } = Grid;

const Messages = React.memo(({ selectedConversation, onBack, setConversations, setSelectedConversation }) => {
  const screens = useBreakpoint();
  const { handleError } = useErrorHandler();
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [localConversation, setLocalConversation] = useState(selectedConversation);
  const { isDarkMode } = useTheme();
  const themeColors = {
    background: isDarkMode ? '#141414' : '#fff',
  };

  // Update local conversation when prop changes
  useEffect(() => {
    setLocalConversation(selectedConversation);
  }, [selectedConversation]);

  const { connectionState } = useSocket();
  
  // Memoize conversation ID for optimized subscription
  const conversationIds = useMemo(() => 
    selectedConversation?.id ? [selectedConversation.id] : []
  , [selectedConversation?.id]);
  
  // Memoize contact ID for subscription
  const contactIds = useMemo(() => 
    selectedConversation?.contact?.id ? [selectedConversation.contact.id] : []
  , [selectedConversation?.contact?.id]);
  
  const isSubscribed = useSocketSubscription(Resource.CONVERSATION, conversationIds, true);
  
  // Subscribe to contact updates
  useSocketSubscription(Resource.CONTACT, contactIds, true);
  
  const {
    isIgnored,
    isSnoozed,
    isAdmin,
    updateContactStatus
  } = useContactStatus(selectedConversation, setConversations, setSelectedConversation);

  const {
    messages,
    loading,
    hasMore,
    messageRefs,
    handleScroll,
    setMessages
  } = useMessageFetching(selectedConversation, handleError);
  const messagesIds = useMemo(() => {
    return messages.map(message => message.id)
  }, [messages]);
  useSocketSubscription(Resource.MESSAGE, messagesIds, true);

  // Handle new messages
  const handleNewMessage = useCallback((data) => {
    console.log('handleNewMessage', data);
    if (data.conversation_id === selectedConversation?.id) {
      setMessages(prev => [data, ...prev]);
    }
  }, [selectedConversation?.id, setMessages]);

  // Register socket event listeners
  useSocketEvent(Resource.MESSAGE, Action.CREATE, handleNewMessage);

  useSocketEvent(Resource.MESSAGE, Action.UPDATE, (data) => {
    if (data.conversation_id === selectedConversation?.id) {
      setMessages(prev => prev.map(msg =>
        msg.id === data.id ? { ...msg, ...data } : msg
      ));
    }
  });

  useSocketEvent(Resource.MESSAGE, Action.DELETE, (data) => {
    if (data.conversation_id === selectedConversation?.id) {
      setMessages(prev => prev.filter(msg => msg.id !== data.id));
    }
  });

  useSocketEvent(Resource.CONVERSATION, Action.UPDATE, (data) => {
    if (data.id === selectedConversation?.id) {
      setLocalConversation(prev => ({
        ...prev,
        ...data
      }));
    }
  });

  // Handle contact updates
  useSocketEvent(Resource.CONTACT, Action.UPDATE, (data) => {
    console.log('handleContactUpdate', data);
    if (data.id === selectedConversation?.contact?.id) {
      setSelectedConversation(prev => ({
        ...prev,
        contact: {
          ...prev.contact,
          ...data
        }
      }));
      
      // Update the conversation in the parent component
      if (setSelectedConversation) {
        setSelectedConversation(current => ({
          ...current,
          contact: {
            ...current.contact,
            ...data
          }
        }));
      }
    }
    
    // Update all conversations in the list with this contact
    if (setConversations) {
      setConversations(prevConversations => 
        prevConversations.map(conversation => 
          conversation.contact?.id === data.id 
            ? {
                ...conversation,
                contact: {
                  ...conversation.contact,
                  ...data
                }
              } 
            : conversation
        )
      );
    }
  });

  const handleReplyClick = useCallback((replyTo) => {
    const originalMessageId = messages.find(
      m => m.sent_at === replyTo.sent_at && m.sender_type === replyTo.sender_type
    )?.id;

    if (originalMessageId && messageRefs.current[originalMessageId]?.current) {
      messageRefs.current[originalMessageId].current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [messages, messageRefs]);

  const handleSendMessage = useCallback(async (messageData) => {
    setIsSending(true);
    try {
      const response = await sendMessage(selectedConversation?.id, { 
        content: messageData.message,
        attachment_ids: messageData.attachment_ids || []
      });
      // Handle successful message send
    } catch (error) {
      handleError(error);
    } finally {
      setIsSending(false);
    }
  }, [selectedConversation?.id, handleError]);

  const handleUploadDocument = (file) => {
    // Implement your logic to upload the document
  };

  if (!connectionState.status === 'connected') {
    return <div>Socket disconnected. Trying to reconnect...</div>;
  }

  return (
    <Layout style={{ height: '100%', width: '100%', background: themeColors.background }}>
      <MessageHeader
        screens={screens}
        onBack={onBack}
        isIgnored={isIgnored}
        isSnoozed={isSnoozed}
        isAdmin={isAdmin}
        updateContactStatus={updateContactStatus}
        selectedConversation={localConversation}
      />

      <MessageContent
        screens={screens}
        selectedConversation={localConversation}
        messages={messages}
        loading={loading}
        hasMore={hasMore}
        messageRefs={messageRefs}
        handleScroll={handleScroll}
        handleReplyClick={handleReplyClick}
        setSelectedMessage={setSelectedMessage}
        setDrawerVisible={setDrawerVisible}
      />

      <MessageDetailsDrawer
        screens={screens}
        drawerVisible={drawerVisible}
        selectedMessage={selectedMessage}
        setDrawerVisible={setDrawerVisible}
        setSelectedMessage={setSelectedMessage}
      />
      <MessageFooter
        onSendMessage={handleSendMessage}
        onUploadDocument={handleUploadDocument}
        isSending={isSending}
      />
    </Layout>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.selectedConversation?.id === nextProps.selectedConversation?.id &&
    prevProps.onBack === nextProps.onBack &&
    prevProps.setConversations === nextProps.setConversations &&
    prevProps.setSelectedConversation === nextProps.setSelectedConversation
  );
});

export default Messages;
